<template>
  <div id="app" ref="app" :data-mode="$store.state.mode" :data-mounted="ready">
    <div class="view-container" ref="container" v-if="fontLoaded">
      <router-view :key="$route.fullPath" />
    </div>

    <div class="mode">
      <div class="mode__switch" @click="switchMode">
        <div class="mode__icon mode__icon--switch"></div>
        <span v-if="$store.state.lang === 'en'">Change Mode</span>
        <span v-if="$store.state.lang === 'es'">Modalidad</span>
      </div>

      <div class="mode__coords">
        <!-- <span>25.804°N 800.199°W</span> -->
        <a href="/en" :class="{ active: $store.state.lang === 'en' }">
          <span>EN</span>
        </a>
        <div class="mode__coords-separator">|</div>
        <a href="/es" :class="{ active: $store.state.lang === 'es' }">
          <span>ES</span>
        </a>
      </div>

      <div class="mode__contact" @click="openContact">
        <span v-if="$store.state.lang === 'en'">Contact Us</span>
        <span v-if="$store.state.lang === 'es'">Contáctanos</span>
        <div class="mode__icon mode__icon--contact"></div>
      </div>
    </div>

    <div class="webgl-container" data-webgl-container>
      <canvas data-webgl-canvas></canvas>
    </div>

    <ContactForm />

    <Cookiebar
      class="app__cookie"
    >
      <CookieContent
        slot="content"
        text=""
        :url="$store.state.lang === 'en' ? 'https://www.iubenda.com/privacy-policy/13526818/cookie-policy' : 'https://www.iubenda.com/privacy-policy/57022479/cookie-policy'"
        label="This site uses cookies for a better experience."
      />
      <CookieButton
        slot="button"
        label="Accept"
      />
    </Cookiebar>

    <CursorContainer />
  </div>
</template>

<script>
import { debounce } from 'lodash';
import gsap from 'gsap';
import FontFaceObserver from 'fontfaceobserver';
import CursorContainer from '@/components/CursorContainer.vue';
import ContactForm from '@/components/ContactForm.vue';
import hades from '@/scripts/hades';
import medusa from '@/scripts/medusa';
import apollo from '@/scripts/apollo';
import Cookiebar from '@/components/Cookie/index.vue';
import CookieContent from '@/components/Cookie/CookieContent/index.vue';
import CookieButton from '@/components/Cookie/CookieButton/index.vue';
// import overlay from '@/scripts/webgl';

export default {
  name: 'App',

  components: {
    CursorContainer,
    ContactForm,
    Cookiebar,
    CookieContent,
    CookieButton,
  },

  data() {
    return {
      fontLoaded: false,
      ready: false,
      isSwitching: false,
    };
  },

  mounted() {
    setTimeout(() => { this.ready = true; }, 200);
    const fontNormal = new FontFaceObserver('GT America', {
      weight: 300,
    });
    const fontBold = new FontFaceObserver('GT America', {
      weight: 'normal',
    });
    Promise.all([fontNormal.load(), fontBold.load()]).then(() => {
      this.fontLoaded = true;
      this.$nextTick(() => {
        if (hades.ref === null) hades.init(this.$refs.app, this.$refs.container);
        if (medusa.ref === null) medusa.init();
        if (apollo.ref === null) apollo.init();
        // if (overlay.ref === null) overlay.init();

        this.resize();

        this.enter();
      });
    });

    window.addEventListener('resize', debounce(this.resize, 200));
  },

  methods: {
    resize() {
      const vh = window.innerHeight * 0.01;
      const rw = window.innerWidth / 1920;
      const rh = window.innerHeight / 1080;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
      document.documentElement.style.setProperty('--rw', Math.max(rw, 0.6));
      document.documentElement.style.setProperty('--rh', Math.max(rh, 0.6));
      this.$root.$emit('resize');
      // overlay.ref.resize();
    },
    enter() {
      this.$nextTick(() => {
        const medusaNodes = [...this.$el.querySelectorAll('[data-viewport]')];
        medusa.addNodes(medusaNodes);

        const medusaHTMLNodes = [...this.$el.querySelectorAll('[data-medusa-animation]')];
        medusa.addHTMLNodes(medusaHTMLNodes);
      });
    },
    switchMode() {
      if (this.isSwitching) return;
      this.isSwitching = true;
      const view = this.$el.querySelector('.view');
      const tl = gsap.timeline({ defaults: { duration: 1.0, ease: 'expo.inOut' } });
      tl.to(view, { y: -500, autoAlpha: 0 });
      tl.add(() => {
        this.$store.commit('SWITCH_MODE');
        this.$nextTick(() => {
          this.$root.$emit('mode-switch');
        });
        hades.ref.scrollTo({ x: 0, y: 0 }, 0);
      });
      tl.set(view, { y: 500 });
      tl.to(view, { y: 0, autoAlpha: 1 });
      tl.then(() => { this.isSwitching = false; });
    },
    openContact() {
      this.$store.commit('SET_CONTACT', true);
    },
  },
};
</script>

<style lang="scss">
@import "@/styles/base.scss";
@import "@/styles/common.scss";

.webgl-container {
  position: fixed;
  top: 0px;
  left: 0px;
  width: 100vw;
  height: 100vh;
  height: calc(var(--vh, 1vh) * 100);

  pointer-events: none;
}

.mode {
  position: absolute;
  width: 100%;
  top: calc((100vh / 18) * 1);
  padding: 0px calc((100vw / 32) * 1);

  display: flex;
  align-items: center;
  justify-content: space-between;

  overflow: hidden;

  z-index: 2;
  mix-blend-mode: difference;

  &__switch,
  &__contact,
  &__coords {
    transform: translateY(-100%);
    transition: transform .6s $inOutCubic;
    transition-delay: .2s;

    @at-root [data-mounted] & {
      transform: translateY(0px);
    }

    @at-root [data-mode="estate"] & {
      color: $color-white;
    }
  }

  &__switch,
  &__contact {
    display: flex;
    align-items: center;
    cursor: pointer;
    padding: 5px 0px;

    span {
      display: inline-block;
      margin: 0px 10px;
    }
  }

  &__contact {
    @include mq($until: ipadP) {
      text-align: right;
    }
  }

  &__coords {
    display: flex;

    @include mq($until: ipadP) {
      text-align: center;
    }

    a.active {
      font-weight: normal;
      border-bottom: 1px solid #FFFFFF;
      padding-bottom: 10px;
    }

    &-separator {
      margin: 0 10px;
    }
  }

  &__icon {
    position: relative;
    top: 2px;
    width: 20px;
    height: 20px;
    background-position: center center;
    background-size: contain;
    background-repeat: no-repeat;

    @at-root [data-mode="estate"] & {
      transform: rotate(-180deg);
    }

    &--switch {
      background-image: url('/static/vector/switch.svg');
    }

    &--contact {
      background-image: url('/static/vector/contact.svg');
    }
  }
}
</style>
